import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TypedSupabaseClient } from '../../lib/supabaseClient';
import {
  ContinuingReviewQueueRow,
  listRegulatoryBasisCodes,
  updateContinuingReviewDecisionMetadata,
} from '../../services/continuingReviewService';
import {
  DEFAULT_WINDOW_DAYS,
  FDA_MAX_INTERVAL_MONTHS,
  hasFdaBasisCode,
  MAX_INTERVAL_MONTHS,
  MIN_INTERVAL_MONTHS,
  NOT_REQUIRED_REASONS,
} from './continuingReviewQueueUtils';

type RegulatoryBasisCode = Awaited<ReturnType<typeof listRegulatoryBasisCodes>>[number];

interface CrMetadataModalProps {
  row: ContinuingReviewQueueRow;
  supabase: TypedSupabaseClient;
  onClose: () => void;
  /** Called after the determination is saved so the queue can refresh. */
  onSaved: () => void;
}

const AUTHORITY_ORDER = ['HHS', 'FDA', 'OTHER'];

const CrMetadataModal: React.FC<CrMetadataModalProps> = ({ row, supabase, onClose, onSaved }) => {
  const [required, setRequired] = useState<boolean>(row.continuing_review_required ?? true);
  const [intervalMonths, setIntervalMonths] = useState<string>(
    row.continuing_review_interval_months != null ? String(row.continuing_review_interval_months) : '12'
  );
  const [windowDays, setWindowDays] = useState<string>(String(DEFAULT_WINDOW_DAYS));
  const [basis, setBasis] = useState<string>(row.continuing_review_basis ?? '');
  const [rationale, setRationale] = useState<string>(row.continuing_review_rationale ?? '');
  const [notRequiredReason, setNotRequiredReason] = useState<string>(
    row.continuing_review_not_required_reason ?? ''
  );
  const [selectedCodes, setSelectedCodes] = useState<string[]>(row.regulatory_basis_codes ?? []);

  const [codes, setCodes] = useState<RegulatoryBasisCode[]>([]);
  const [codesLoading, setCodesLoading] = useState(true);

  const [validationError, setValidationError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const loadCodes = useCallback(async () => {
    setCodesLoading(true);
    try {
      const result = await listRegulatoryBasisCodes(supabase);
      setCodes(result as RegulatoryBasisCode[]);
    } finally {
      setCodesLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    loadCodes();
  }, [loadCodes]);

  const groupedCodes = useMemo(() => {
    const groups = new Map<string, RegulatoryBasisCode[]>();
    for (const code of codes) {
      const list = groups.get(code.authority) ?? [];
      list.push(code);
      groups.set(code.authority, list);
    }
    const authorities = Array.from(groups.keys()).sort(
      (a, b) => AUTHORITY_ORDER.indexOf(a) - AUTHORITY_ORDER.indexOf(b)
    );
    return authorities.map((authority) => ({ authority, items: groups.get(authority) ?? [] }));
  }, [codes]);

  const fdaSelected = hasFdaBasisCode(selectedCodes);

  const toggleCode = (code: string) => {
    setSelectedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleSave = async () => {
    setValidationError(null);
    setServerError(null);

    if (!row.source_decision_id) {
      setValidationError('This row has no source decision to update.');
      return;
    }

    let intervalValue: number | null = null;
    if (required) {
      intervalValue = Number(intervalMonths);
      if (!Number.isInteger(intervalValue) || intervalValue < MIN_INTERVAL_MONTHS || intervalValue > MAX_INTERVAL_MONTHS) {
        setValidationError(`Interval must be between ${MIN_INTERVAL_MONTHS} and ${MAX_INTERVAL_MONTHS} months.`);
        return;
      }
      if (fdaSelected && intervalValue > FDA_MAX_INTERVAL_MONTHS) {
        setValidationError(`FDA basis codes cap the review interval at ${FDA_MAX_INTERVAL_MONTHS} months.`);
        return;
      }
    } else if (!notRequiredReason) {
      setValidationError('Select a reason why continuing review is not required.');
      return;
    }

    const windowValue = windowDays.trim().length > 0 ? Number(windowDays) : DEFAULT_WINDOW_DAYS;
    if (required && (!Number.isInteger(windowValue) || windowValue <= 0)) {
      setValidationError('Window days must be a positive whole number.');
      return;
    }

    setSaving(true);
    try {
      await updateContinuingReviewDecisionMetadata(supabase, {
        reviewDecisionId: row.source_decision_id,
        continuingReviewRequired: required,
        intervalMonths: required ? intervalValue : null,
        notRequiredReason: required ? null : notRequiredReason,
        windowDays: required ? windowValue : null,
        basis: required ? basis.trim() || null : null,
        rationale: rationale.trim() || null,
        regulatoryBasisCodes: required ? selectedCodes : [],
      });
      onSaved();
      onClose();
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'Unable to save continuing review metadata.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Record continuing review metadata"
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between border-b px-6 py-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Record continuing review metadata</h3>
            <p className="mt-1 text-sm text-gray-500">{row.protocol_title || 'Untitled protocol'}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            ✕
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
            />
            Continuing review required
          </label>

          {required ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="cr-interval">
                  Interval (months)
                </label>
                <input
                  id="cr-interval"
                  type="number"
                  min={MIN_INTERVAL_MONTHS}
                  max={MAX_INTERVAL_MONTHS}
                  className="mt-1 w-32 border rounded-md px-3 py-2 text-sm"
                  value={intervalMonths}
                  onChange={(e) => setIntervalMonths(e.target.value)}
                />
                {fdaSelected && (
                  <p className="mt-1 text-xs text-amber-700">
                    An FDA basis code is selected — interval is capped at {FDA_MAX_INTERVAL_MONTHS} months.
                  </p>
                )}
              </div>

              <div>
                <span className="block text-sm font-medium text-gray-700 mb-1">Regulatory basis codes</span>
                {codesLoading ? (
                  <p className="text-sm text-gray-500">Loading codes…</p>
                ) : groupedCodes.length === 0 ? (
                  <p className="text-sm text-gray-500">No regulatory basis codes available.</p>
                ) : (
                  <div className="space-y-3">
                    {groupedCodes.map((group) => (
                      <fieldset key={group.authority}>
                        <legend className="text-xs font-semibold uppercase text-gray-500">
                          {group.authority}
                        </legend>
                        <div className="mt-1 space-y-1">
                          {group.items.map((code) => (
                            <label key={code.code} className="flex items-start gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                className="mt-0.5"
                                checked={selectedCodes.includes(code.code)}
                                onChange={() => toggleCode(code.code)}
                              />
                              <span>
                                <span className="font-mono text-xs">{code.code}</span>
                                {code.description ? ` — ${code.description}` : ''}
                              </span>
                            </label>
                          ))}
                        </div>
                      </fieldset>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="cr-window">
                  Window days
                </label>
                <input
                  id="cr-window"
                  type="number"
                  min={1}
                  className="mt-1 w-32 border rounded-md px-3 py-2 text-sm"
                  value={windowDays}
                  onChange={(e) => setWindowDays(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="cr-basis">
                  Basis
                </label>
                <input
                  id="cr-basis"
                  type="text"
                  className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                  value={basis}
                  onChange={(e) => setBasis(e.target.value)}
                />
              </div>
            </>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="cr-not-required-reason">
                Not-required reason
              </label>
              <select
                id="cr-not-required-reason"
                className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                value={notRequiredReason}
                onChange={(e) => setNotRequiredReason(e.target.value)}
              >
                <option value="">Select a reason…</option>
                {NOT_REQUIRED_REASONS.map((reason) => (
                  <option key={reason.value} value={reason.value}>
                    {reason.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="cr-rationale">
              Rationale
            </label>
            <textarea
              id="cr-rationale"
              className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
              rows={3}
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
            />
          </div>

          {validationError && (
            <p className="text-sm text-red-600" role="alert">
              {validationError}
            </p>
          )}
          {serverError && (
            <p className="text-sm text-red-600" role="alert">
              {serverError}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t px-6 py-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 border rounded-md hover:bg-gray-50"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm text-white bg-[#F55D3E] rounded-md hover:bg-[#F9A594] disabled:opacity-50"
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CrMetadataModal;
